import React, { useEffect } from "react";
import ModalActions from "components/ModalActions";
import ConnectedField from "components/ConnectedField";
import { Formik } from "formik";
import Toggle from "react-toggle";
import { FORM_KEY, TOGGLE_STATE } from "utils/constants/form";
import Pill from "components/Pill";
import { getIconForUID } from "components/FormerEditor/utils";
import TextareaController from "components/TextareaController";
import { submitOnReturn, integerTransformer } from "utils/form";
import { getEntitlementFormIntialValues } from "utils/entitlements";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import omit from "lodash/omit";

const ENABLED_KEY = "enabled";
const LIMIT_MAX_KEY = "limit.max";
const FORM_LIMIT_KEY = `${FORM_KEY}.limit`;
const FORM_LINK_KEY = `${FORM_KEY}.link`;
const Icon = getIconForUID("right");

const CellEntitlementForm = ({
  feature,
  entitlement,
  product,
  updateEntitlement,
  featureGroup,
  setShowModal
}) => {
  const initialValues = getEntitlementFormIntialValues(entitlement);

  return (
    <Formik initialValues={initialValues}>
      {({ values, setValues, setFieldValue, setFieldTouched, touched }) => {
        const active = Boolean(values && values[ENABLED_KEY]);
        const isTouched = Boolean(touched[ENABLED_KEY]);
        const activeToggleProps = {
          icons: false,
          checked: active,
          value: active ? TOGGLE_STATE.YES : TOGGLE_STATE.NO,
          onChange: (evt) => {
            const { value } = evt.currentTarget;
            const active = value !== TOGGLE_STATE.YES;
            setFieldValue(ENABLED_KEY, active);
            if (!isTouched) {
              setFieldTouched(ENABLED_KEY, true);
            }
          }
        };

        const limitEnabled = Boolean(get(values, FORM_LIMIT_KEY));
        const limitEnabledTouched = Boolean(get(touched, FORM_LIMIT_KEY));
        const limitToggleProps = {
          icons: false,
          checked: limitEnabled,
          value: limitEnabled ? TOGGLE_STATE.YES : TOGGLE_STATE.NO,
          onChange: (evt) => {
            const { value } = evt.currentTarget;
            const limitEnabled = value !== TOGGLE_STATE.YES;
            setFieldValue(FORM_LIMIT_KEY, limitEnabled);
            if (!limitEnabledTouched) {
              setFieldTouched(FORM_LIMIT_KEY, true);
            }
          }
        };

        const linkEnabled = Boolean(get(values, FORM_LINK_KEY));
        const linkEnabledTouched = Boolean(get(touched, FORM_LINK_KEY));
        const linkToggleProps = {
          icons: false,
          checked: linkEnabled,
          value: linkEnabled ? TOGGLE_STATE.YES : TOGGLE_STATE.NO,
          onChange: (evt) => {
            const { value } = evt.currentTarget;
            const linkEnabled = value !== TOGGLE_STATE.YES;
            setFieldValue(FORM_LINK_KEY, linkEnabled);
            if (!linkEnabledTouched) {
              setFieldTouched(FORM_LINK_KEY, true);
            }
          }
        };
        const maxValue = get(values, LIMIT_MAX_KEY);

        const maxDefined = typeof maxValue !== "undefined";
        const valueLink = Boolean(get(values, FORM_LINK_KEY));
        const disableValueInput = valueLink && limitEnabled;

        /**
         * Auto enable/disable entitlement when value transitions
         */
        useEffect(() => {
          let updatedValues = cloneDeep(values);
          let shouldUpdate = false;
          if (limitEnabled && !maxDefined) {
            shouldUpdate = true;
            updatedValues = {
              ...updatedValues,
              limit: {
                max: 1,
                unit: "number",
                subunit: null
              }
            };
            if (valueLink) {
              updatedValues.value = 1;
            }
          } else if (!limitEnabled && maxDefined) {
            shouldUpdate = true;
            updatedValues = {
              [FORM_KEY]: updatedValues[FORM_KEY],
              enabled: updatedValues.enabled,
              value: "",
              description: updatedValues.description,
              limit: null
            };
          }
          if (shouldUpdate) {
            setValues(updatedValues);
            setFieldTouched(LIMIT_MAX_KEY, true);
          }
        }, [limitEnabled, maxDefined]);

        useEffect(() => {
          const maxAsString = `${maxValue}`;
          if (
            limitEnabled &&
            valueLink &&
            maxDefined &&
            values.value !== maxAsString
          ) {
            setFieldValue("value", maxAsString);
          }
        }, [values.value, maxValue, valueLink, limitEnabled, maxDefined]);

        const submitForm = () => updateEntitlement(omit(values, [FORM_KEY]));

        return (
          <div className="w-100 h-100 h-auto-ns center ph3 pb3">
            <div className="w-100 bb hairline-1 pb3">
              <div className="f2">{feature.title}</div>
              <div className="flex flex-row pt3 items-center">
                <div className="f6 fw5">Group</div>
                <div className="pl2">
                  <Pill
                    customClasses={{
                      container: "dib"
                    }}
                    theme="slim"
                    copy={featureGroup.title}
                  />
                </div>
                <div className="ph1 items-center justify-center flex">
                  <Icon size={20} />
                </div>
                <div className="f6 fw5">Product</div>
                <div className="pl2">
                  <Pill
                    customClasses={{
                      container: "dib"
                    }}
                    theme="slim"
                    copy={product.name}
                  />
                </div>
              </div>
            </div>
            <div className="pv3 bb hairline-1 flex flex-row items-center justify-between">
              <label className="field__label fw6 db">{`Included in ${product.name}`}</label>
              <Toggle {...activeToggleProps} />
            </div>
            <div className="pv3 bb hairline-1">
              <div className="flex flex-row flex flex-row items-center justify-between">
                <label className="field__label fw6 db">Limit settings</label>
                <Toggle {...limitToggleProps} />
              </div>
              {limitEnabled && (
                <div className="mt2 ph2 pv3 bg-whitesmoke ba hairline-1 br2">
                  <div className="flex flex-row justify-between items-center pb2">
                    <label className="fw5 db f7">Max</label>
                    <div className="w4">
                      <ConnectedField
                        inputMode="numeric"
                        hideError
                        name="limit.max"
                        transformer={integerTransformer}
                      />
                    </div>
                  </div>
                  <div className="flex flex-row flex flex-row items-center justify-between pt2">
                    <label className="fw5 db f7">Copy to value</label>
                    <Toggle {...linkToggleProps} />
                  </div>
                </div>
              )}
            </div>
            <div className="pv3 bb hairline-1">
              <TextareaController
                name="value"
                label="Value"
                disabled={disableValueInput}
                minRows={1}
                onKeyPress={submitOnReturn({ submitForm })}
              />
            </div>
            <div className="pv3 bb hairline-1">
              <TextareaController
                name="description"
                label="Description"
                minRows={1}
                onKeyPress={submitOnReturn({ submitForm })}
              />
            </div>
            <ModalActions
              actions={[
                {
                  cta: "Cancel",
                  onClick: () => {
                    setShowModal(false);
                  }
                },
                {
                  cta: "Update",
                  disabled: isEmpty(touched),
                  onClick: submitForm
                }
              ]}
            />
          </div>
        );
      }}
    </Formik>
  );
};

export default CellEntitlementForm;
